import { supabase } from "@/lib/supabase/client";
import { readWinbackLinkContext, readWinbackOpaqueId } from "./trace";
import { noticeAttributionState } from "@/lib/analytics/noticeAttribution";
import { env } from "@/lib/env";

const KEY = "mpr_winback_entry_v1";
type Entry = { version: 1; traceId: string; entryId: string; expiresAt: number; hardExpiresAt: number; observed: boolean;
  actorId: string | null; householdId: string | null };
type Dependencies = { storage: Pick<Storage, "getItem" | "setItem" | "removeItem">; now: () => number; uuid: () => string;
  canRecord: () => boolean;
  canRestore?: (entry: Entry) => boolean;
  request: (body: Record<string, unknown>) => Promise<Record<string, unknown> | null>;
  requestHandoff?: (body: Record<string, unknown>) => Promise<unknown> };

/** One tab, one most-recent external entry. Storage is optional; no cookie or fingerprint. */
export function createWebWinbackEntry(deps: Dependencies) {
  let memory: Entry | null = null;
  let loaded = false;
  let observation: { id: string; promise: Promise<Entry | null> } | null = null;
  let resolution: { key: string; promise: Promise<void> } | null = null;
  let expectedActor: string | null = null;
  const viewed = new Set<string>();
  const selectedPlans = new Set<string>();
  const actions = new Map<string, Promise<void>>();
  function save(value: Entry | null) {
    if (memory?.entryId !== value?.entryId) selectedPlans.clear();
    memory = value; loaded = true;
    try { if (value) deps.storage.setItem(KEY, JSON.stringify(value)); else deps.storage.removeItem(KEY); } catch { /* memory fallback */ }
  }
  function current(traceId?: string): Entry | null {
    if (!deps.canRecord()) { expectedActor = null; save(null); return null; }
    if (!loaded) {
      loaded = true;
      try {
        const raw = JSON.parse(deps.storage.getItem(KEY) ?? "null");
        if (raw?.version === 1 && readWinbackOpaqueId(raw.traceId) && readWinbackOpaqueId(raw.entryId)
          && typeof raw.expiresAt === "number" && typeof raw.hardExpiresAt === "number" && typeof raw.observed === "boolean"
          && (raw.actorId === null || readWinbackOpaqueId(raw.actorId))
          && (raw.householdId === null || readWinbackOpaqueId(raw.householdId))
          && (deps.canRestore?.(raw) ?? true)) memory = raw;
        if (raw && !memory) save(null);
      } catch { /* corrupt/blocked storage does not block the app */ }
    }
    if (memory && ((traceId && memory.traceId !== traceId) || memory.expiresAt <= deps.now() || memory.hardExpiresAt <= deps.now())) save(null);
    return memory;
  }
  function ensure(traceId: string): Promise<Entry | null> {
    if (!deps.canRecord()) { save(null); return Promise.resolve(null); }
    if (!readWinbackOpaqueId(traceId)) return Promise.resolve(null);
    let entry = current(traceId);
    if (entry?.observed) return Promise.resolve(entry);
    if (entry && observation?.id === entry.entryId) return observation.promise;
    if (!entry) {
      entry = { version: 1, traceId, entryId: deps.uuid(), expiresAt: deps.now() + 24 * 3600_000,
        hardExpiresAt: deps.now() + 24 * 3600_000,
        observed: false, actorId: null, householdId: null };
      save(entry); // Persist before the request so uncertain retries use exactly this ID.
    }
    const captured = entry;
    const promise = (async () => {
      try {
        const result = await deps.request({ operation: "observe", traceId, entryId: captured.entryId, platform: "web" });
        if (!deps.canRecord() || memory?.entryId !== captured.entryId) return null;
        if (result?.status !== "observed" || result.entryId !== captured.entryId || typeof result.expiresAt !== "string") return null;
        const expiresAt = Date.parse(result.expiresAt);
        if (!Number.isFinite(expiresAt) || expiresAt <= deps.now()) return null;
        const observed = { ...captured, observed: true, expiresAt: Math.min(captured.expiresAt, expiresAt),
          hardExpiresAt: Math.min(captured.hardExpiresAt, expiresAt) };
        save(observed); return observed;
      } catch { return null; }
      finally { if (observation?.id === captured.entryId) observation = null; }
    })();
    observation = { id: captured.entryId, promise }; return promise;
  }
  async function resolve(traceId: string, actorId: string) {
    expectedActor = actorId;
    let entry = current(traceId);
    if (!entry || !readWinbackOpaqueId(actorId)) return;
    if (entry.actorId && entry.actorId !== actorId) { save(null); return; }
    if (entry.actorId === actorId) return;
    if (!entry.observed) { entry = await ensure(traceId); if (!entry) return; }
    const captured = entry; const key = `${entry.entryId}:${actorId}`;
    if (resolution?.key === key) return resolution.promise;
    const promise = (async () => {
      try {
        const result = await deps.request({ operation: "resolve", traceId, entryId: captured.entryId });
        if (!deps.canRecord() || expectedActor !== actorId || memory?.entryId !== captured.entryId || (memory.actorId && memory.actorId !== actorId)) return;
        if (result?.status !== "resolved" || result.entryId !== captured.entryId || result.actorUserId !== actorId || !readWinbackOpaqueId(result.householdId)
          || typeof result.expiresAt !== "string") return;
        const expiresAt = Date.parse(result.expiresAt);
        if (Number.isFinite(expiresAt) && expiresAt > deps.now()) save({ ...memory, actorId, householdId: result.householdId as string,
          expiresAt: Math.min(memory.expiresAt, expiresAt) });
      } catch { /* attribution failure does not block period read/login */ }
      finally { if (resolution?.key === key) resolution = null; }
    })();
    resolution = { key, promise }; return promise;
  }
  function search(traceId: string) {
    const params = new URLSearchParams({ mpr_trace: traceId });
    const entry = current(traceId);
    if (entry) params.set("mpr_entry", entry.entryId);
    return params.toString();
  }
  async function handoff(traceId: string, trigger: "auto" | "explicit") {
    await ensure(traceId);
    const entry = current(traceId);
    const params = new URLSearchParams(search(traceId));
    params.set("mpr_handoff", trigger);
    if (entry) {
      // Keep the claim even on a failed observation. Native must classify that as
      // unknown, never as a verified explicit/automatic web handoff.
      try { await deps.request({ operation: "handoff", traceId, entryId: entry.entryId, handoff: trigger }); } catch { /* safe fallback */ }
    }
    return params.toString();
  }
  /** Starts the optional audit request during the user's click, without delaying the OS link. */
  function handoffOnClick(traceId: string): string {
    const params = new URLSearchParams(search(traceId));
    params.set("mpr_handoff", "explicit");
    const entry = current(traceId);
    if (entry?.observed) {
      const body = { operation: "handoff", traceId, entryId: entry.entryId, handoff: "explicit" };
      try { void (deps.requestHandoff ?? deps.request)(body).catch(() => undefined); } catch { /* navigation is independent */ }
    }
    return params.toString();
  }
  function syncIdentity(actorId: string | null) {
    const entry = current();
    if (entry?.actorId && entry.actorId !== actorId) { expectedActor = null; save(null); }
  }
  function syncHousehold(householdId: string) {
    const entry = current();
    if (entry?.householdId && entry.householdId !== householdId) { expectedActor = null; save(null); }
  }
  function forAction(actorId: string | undefined, householdId: string | null) {
    const entry = current();
    if (!entry?.actorId) return null;
    if (entry.actorId !== actorId || entry.householdId !== householdId) { expectedActor = null; save(null); return null; }
    return { traceId: entry.traceId, entryId: entry.entryId };
  }
  function applyActivityReceipt(context: { traceId: string; entryId: string }, actorId: string | undefined,
    householdId: string | null, receipt: unknown) {
    const entry = current();
    if (!entry || entry.entryId !== context.entryId || entry.traceId !== context.traceId
      || entry.actorId !== actorId || entry.householdId !== householdId || !receipt || typeof receipt !== "object") return;
    const result = receipt as Record<string, unknown>;
    if (!["verified", "recorded"].includes(String(result.status)) || result.entryId !== entry.entryId || typeof result.expiresAt !== "string") return;
    const expiresAt = Date.parse(result.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt > deps.now()) save({ ...entry,
      expiresAt: Math.min(entry.hardExpiresAt, Math.max(entry.expiresAt, expiresAt)) });
  }
  async function activity(traceId: string, actorId: string, householdId: string, action: "period_viewed" | "routines_opened" | "billing_opened") {
    await resolve(traceId, actorId);
    const context = forAction(actorId, householdId);
    if (!context || context.traceId !== traceId || (action === "period_viewed" && viewed.has(context.entryId))) return;
    const key = `${context.entryId}:${action}`;
    const pending = actions.get(key); if (pending) return pending;
    const promise = (async () => {
      try {
        const result = await deps.request({ operation: "activity", ...context, householdId, action, eventId: deps.uuid() });
        applyActivityReceipt(context, actorId, householdId, result);
        if (action === "period_viewed" && result?.status === "recorded" && result.entryId === context.entryId) viewed.add(context.entryId);
      } catch { /* UI actions remain available without analytics */ }
      finally { actions.delete(key); }
    })();
    actions.set(key,promise); return promise;
  }
  async function selectPlan(actorId: string | undefined, householdId: string | null, selectedPlan: "basic" | "pro") {
    if (selectedPlan !== "basic" && selectedPlan !== "pro") return;
    const context = forAction(actorId, householdId);
    if (!context) return;
    const key = `${context.entryId}:plan_selected:${selectedPlan}`;
    if (selectedPlans.has(key)) return;
    const pending = actions.get(key); if (pending) return pending;
    const promise = (async () => {
      try {
        const result = await deps.request({ operation: "plan_selection", ...context, householdId,
          selectedPlan, eventId: deps.uuid() });
        applyActivityReceipt(context, actorId, householdId, result);
        if (result?.status === "recorded" && result.entryId === context.entryId
          && forAction(actorId, householdId)?.entryId === context.entryId) selectedPlans.add(key);
      } catch { /* analytics failure must not change plan selection or checkout */ }
      finally { actions.delete(key); }
    })();
    actions.set(key, promise); return promise;
  }
  return { ensure, resolve, search, handoff, handoffOnClick, activity, forAction, applyActivityReceipt, syncIdentity, syncHousehold,
    selectPlan, clear: () => { expectedActor = null; save(null); }, current };
}

let singleton: ReturnType<typeof createWebWinbackEntry> | undefined;
export function getWebWinbackEntry() {
  if (!singleton) singleton = createWebWinbackEntry({
    storage: { getItem: key => window.sessionStorage.getItem(key), setItem: (key, value) => window.sessionStorage.setItem(key, value),
      removeItem: key => window.sessionStorage.removeItem(key) }, now: Date.now, uuid: () => crypto.randomUUID(),
    canRecord: () => noticeAttributionState() === "allowed",
    canRestore: entry => {
      // Guest pending data alone cannot attach to actions. It must survive an
      // OAuth callback without a trace query, then resolve on the actual notice.
      if (!entry.actorId) return true;
      const context = readWinbackLinkContext(window.location.search);
      // A new direct app session must not inherit yesterday's SMS attribution.
      // Guest auth-return and traced refreshes may restore their bounded entry.
      return context?.traceId === entry.traceId && context.entryId === entry.entryId;
    },
    request: async body => {
      const { data, error } = await supabase.functions.invoke("record_basic_winback_entry", { body, timeout: 2000 });
      return error ? null : data;
    },
    requestHandoff: body => fetch(`${env.supabaseUrl}/functions/v1/record_basic_winback_entry`, {
      method: "POST", body: JSON.stringify(body), keepalive: true,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
    }),
  });
  return singleton;
}
